import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface UserLookup {
  id: string;
  email: string;
  /** Resolved display name (falls back to email when no name parts are set). */
  name: string;
  /** Real firstName + lastName, when set. Shown alongside `name` in admin views so operators can disambiguate cryptic display names. */
  fullName?: string | null;
  /** The raw displayName the user set, if any. */
  displayName?: string | null;
}

/**
 * One selectable person in an admin name picker — the payload of
 * `GET /users/organization/:organizationId/coach-options`.
 *
 * "Coach" is the role in the picker, not a role in the system: the endpoint
 * returns every member of the organization, because there is no trainer/coach
 * role to filter on. The name says what the list is *for*, so it stays honest
 * if the backend ever narrows the set.
 *
 * Deliberately NOT `UserLookup`: the picker renders `fullName || name` and
 * nothing else, so the endpoint carries no email and none of the personal data
 * (address, birthdate, phone, SEPA, fee/work-hours standing) that the admin
 * member overview does. Typing it as its own shape is what keeps a caller from
 * reaching for a field that is no longer on the wire.
 */
export interface OrganizationCoachOption {
  id: string;
  /** Resolved display name; falls back to the real name, then to the email. */
  name: string;
  /** Real firstName + lastName, when set. Null when neither part is set. */
  fullName: string | null;
}

/**
 * Player search result for multi-player booking selection
 */
export interface PlayerSearchResult {
  id: string;
  name: string;
  /** Real firstName + lastName, when set. */
  fullName?: string | null;
  email: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  getOrganizationUsers(organizationId: string): Observable<UserLookup[]> {
    return this.http.get<UserLookup[]>(`${this.apiUrl}/users/organization/${organizationId}`);
  }

  /**
   * Every member of an organization, reduced to what a name picker needs.
   *
   * Use this — never `getOrganizationUsers` — whenever the UI only draws a list
   * of names. That endpoint answers with the admin member overview (address,
   * birthdate, phone, SEPA mandate state, membership-fee and work-hours
   * standing) and runs eight org-wide queries to build it; a `<select>` shows
   * none of that.
   *
   * Admin-only, org-scoped by the path param. No limit and no role filter:
   * there is no trainer role in the system, so every member is a candidate —
   * the `coach-options` path names the picker it fills, not that unfiltered
   * result, so nothing here has to be renamed if a coach role is ever added.
   */
  getOrganizationCoachOptions(organizationId: string): Observable<OrganizationCoachOption[]> {
    return this.http.get<OrganizationCoachOption[]>(
      `${this.apiUrl}/users/organization/${organizationId}/coach-options`
    );
  }

  /**
   * Search for players in an organization for multi-player booking.
   * Excludes the current user from results.
   * @param organizationId Organization to search within
   * @param searchTerm Name or email to search for
   * @param limit Maximum number of results (default: 10)
   */
  searchPlayers(organizationId: string, searchTerm: string, limit: number = 10): Observable<PlayerSearchResult[]> {
    return this.http.get<PlayerSearchResult[]>(`${this.apiUrl}/users/search`, {
      params: {
        q: searchTerm,
        organizationId,
        limit: limit.toString()
      }
    });
  }
}